/** biome-ignore-all lint/a11y/noRedundantRoles: Explicit roles survive the CSS grid/contents display overrides that remove implicit table semantics in browsers. */ /** biome-ignore-all lint/a11y/useSemanticElements: Native table elements are retained for non-CSS fallbacks while explicit roles restore semantics under grid layout. */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { cn } from '../../../lib/utils';
import { SpecraEyebrow } from '../ensigns/eyebrow';
const levels = [
    'low',
    'medium',
    'high'
];
const impactRows = [
    'high',
    'medium',
    'low'
];
const defaultTone = (impact, probability)=>{
    // 0-4: High/High is critical, one step below it is high, the middle band moderate.
    const severity = levels.indexOf(impact) + levels.indexOf(probability);
    if (severity >= 4) return 'danger';
    if (severity >= 3) return 'orange';
    if (severity >= 2) return 'warning';
    return 'neutral';
};
const cellToneClass = {
    neutral: 'bg-transparent text-[color:var(--text-faint)]',
    warning: 'bg-warning-muted text-warning',
    orange: 'bg-orange-muted text-orange',
    danger: 'bg-danger-muted text-danger'
};
const shortLabel = {
    low: 'Low',
    medium: 'Med',
    high: 'High'
};
/** Nine-cell probability/impact summary from the Delivery risk register. */ export function SpecraRiskMatrix({ axisDescription = 'impact ↑ · probability →', cells, className, getCellLabel = (cell)=>`${shortLabel[cell.impact]} impact, ${shortLabel[cell.probability]} probability: ${cell.count} open ${cell.count === 1 ? 'risk' : 'risks'}`, title = 'Exposure — Open Risks', tableLabel = 'Risk probability and impact matrix', ...props }) {
    const matrixId = React.useId();
    const byCoordinates = new Map(cells.map((cell)=>[
            `${cell.impact}:${cell.probability}`,
            cell
        ]));
    return /*#__PURE__*/ _jsxs("div", {
        className: cn('w-full max-w-[var(--component-risk-matrix-width)]', className),
        ...props,
        children: [
            /*#__PURE__*/ _jsx(SpecraEyebrow, {
                className: "text-[color:var(--text-faint)]",
                children: title
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-[var(--space-3)] flex gap-[var(--space-2)]",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        "aria-hidden": "true",
                        className: "flex flex-col justify-between py-[var(--space-0-5)] text-right text-[length:var(--fs-ui-micro)] uppercase tracking-[var(--ls-ui-label)] text-[color:var(--text-faint)]",
                        children: impactRows.map((level)=>/*#__PURE__*/ _jsx("span", {
                                children: shortLabel[level]
                            }, level))
                    }),
                    /*#__PURE__*/ _jsxs("table", {
                        "aria-label": tableLabel,
                        className: "grid flex-1 grid-cols-3 gap-[var(--space-progress-gap)]",
                        role: "table",
                        children: [
                            /*#__PURE__*/ _jsx("thead", {
                                className: "sr-only",
                                children: /*#__PURE__*/ _jsxs("tr", {
                                    role: "row",
                                    children: [
                                        /*#__PURE__*/ _jsx("th", {
                                            role: "columnheader",
                                            scope: "col",
                                            children: "Impact"
                                        }),
                                        levels.map((probability)=>/*#__PURE__*/ _jsxs("th", {
                                                id: `${matrixId}-probability-${probability}`,
                                                role: "columnheader",
                                                scope: "col",
                                                children: [
                                                    shortLabel[probability],
                                                    " probability"
                                                ]
                                            }, probability))
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx("tbody", {
                                className: "contents",
                                children: impactRows.map((impact)=>/*#__PURE__*/ _jsxs("tr", {
                                        className: "contents",
                                        role: "row",
                                        children: [
                                            /*#__PURE__*/ _jsxs("th", {
                                                className: "sr-only",
                                                id: `${matrixId}-impact-${impact}`,
                                                role: "rowheader",
                                                scope: "row",
                                                children: [
                                                    shortLabel[impact],
                                                    " impact"
                                                ]
                                            }),
                                            levels.map((probability)=>{
                                                const cell = byCoordinates.get(`${impact}:${probability}`) ?? {
                                                    impact,
                                                    probability,
                                                    count: 0
                                                };
                                                const tone = cell.tone ?? defaultTone(impact, probability);
                                                return /*#__PURE__*/ _jsx("td", {
                                                    "aria-label": getCellLabel(cell),
                                                    className: cn('flex h-[var(--component-risk-cell-height)] items-center justify-center rounded-sm border border-[color:var(--border-card)] text-[length:var(--fs-ui-emphasis)] font-medium tabular-nums', cell.count > 0 ? cellToneClass[tone] : cellToneClass.neutral),
                                                    headers: `${matrixId}-impact-${impact} ${matrixId}-probability-${probability}`,
                                                    role: "cell",
                                                    children: cell.count || null
                                                }, `${impact}:${probability}`);
                                            })
                                        ]
                                    }, impact))
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                "aria-hidden": "true",
                className: "ml-[var(--component-risk-axis-indent)] mt-[var(--space-1-5)] flex justify-between text-[length:var(--fs-ui-micro)] uppercase tracking-[var(--ls-ui-label)] text-[color:var(--text-faint)]",
                children: levels.map((level)=>/*#__PURE__*/ _jsx("span", {
                        children: shortLabel[level]
                    }, level))
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "mb-0 ml-[var(--component-risk-axis-indent)] mt-[var(--space-1)] text-[length:var(--fs-ui-micro)] text-[color:var(--text-ghost)]",
                children: axisDescription
            })
        ]
    });
}
